/**
 * Shared chunk navigation footer for simulation modules.
 *
 * Renders the canonical "Previous | indicator dots | Next" footer that
 * Reading uses today. Adopted by Writing (with a "Part X" center label) so
 * the three modules share a single navigation shape.
 *
 * The parent controls the surrounding `bg-exam-*` token via className.
 */

import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight } from "lucide-react";
import type { ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export interface SimulationChunkNavigationProps {
  /** 0-based index of the active chunk. */
  readonly currentIndex: number;
  /** Total number of chunks/sections/tasks. */
  readonly totalChunks: number;
  /** Callback for the previous button. */
  readonly onPrevious: () => void;
  /** Callback for the next button. */
  readonly onNext: () => void;
  /** Full-text label for previous button (desktop); defaults to "Previous Section". */
  readonly previousLabel?: string;
  /** Full-text label for next button (desktop); defaults to "Next Section". */
  readonly nextLabel?: string;
  /**
   * Content rendered between previous/next. Defaults to indicator dots. When
   * provided, replaces the dots — used by Writing to show a "Part X" label.
   */
  readonly centerContent?: ReactNode;
  /** Extra classes appended to the root element. */
  readonly className?: string;
}

export function SimulationChunkNavigation({
  currentIndex,
  totalChunks,
  onPrevious,
  onNext,
  previousLabel,
  nextLabel,
  centerContent,
  className,
}: SimulationChunkNavigationProps) {
  const { t } = useTranslation();
  const previousDefault =
    previousLabel ??
    t("simulation.active.previousSection", {
      defaultValue: "Previous Section",
    });
  const nextDefault =
    nextLabel ??
    t("simulation.active.nextSection", { defaultValue: "Next Section" });

  return (
    <div
      className={cn(
        "safe-area-bottom flex shrink-0 items-center justify-between border-t border-border bg-card/50 px-3 pt-2 sm:px-6 sm:py-4",
        className,
      )}
    >
      <Button
        variant="outline"
        size="sm"
        onClick={onPrevious}
        disabled={currentIndex === 0}
        className="h-11 gap-1 text-xs sm:gap-2 sm:text-sm lg:h-10"
      >
        <ChevronLeft className="w-4 h-4" />
        <span className="hidden sm:inline">{previousDefault}</span>
        <span className="sm:hidden text-[10px] lowercase font-bold">prev</span>
      </Button>

      {centerContent !== undefined ? (
        <div className="font-ui text-[10px] sm:text-sm text-muted-foreground uppercase tracking-wider font-semibold">
          {centerContent}
        </div>
      ) : (
        <div className="flex items-center gap-1.5 sm:gap-2">
          {Array.from({ length: totalChunks }, (_, i) => (
            <div
              key={i}
              className={cn(
                "w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full transition-all duration-300",
                i === currentIndex
                  ? "bg-primary scale-125"
                  : "bg-border hover:bg-muted-foreground/30",
              )}
            />
          ))}
        </div>
      )}

      <Button
        variant="outline"
        size="sm"
        onClick={onNext}
        disabled={currentIndex === totalChunks - 1}
        className="h-11 gap-1 text-xs sm:gap-2 sm:text-sm lg:h-10"
      >
        <span className="hidden sm:inline">{nextDefault}</span>
        <span className="sm:hidden text-[10px] lowercase font-bold">next</span>
        <ChevronRight className="w-4 h-4" />
      </Button>
    </div>
  );
}
